/**
 * Utility functions for time formatting and manipulation using date-fns
 */
import { format, formatDistanceToNow } from 'date-fns';

/**
 * update object type for timestamp collection - only includes timestamp fields
 */
type UpdateWithTimestamps = {
  createdAt: string;
  statusDate: string;
  tasks?: Array<{
    completedAt?: string | null;
  }> | null;
  approvals?: Array<{
    statusDate: string;
    approvedAt?: string | null;
    rejectedAt?: string | null;
  }> | null;
};

/**
 * Collects all available timestamps from a update and its tasks/approvals
 *
 * @param update - update object with createdAt, statusDate, tasks, and approvals
 * @returns Array of ISO timestamp strings
 */
function collectTimestamps(update: UpdateWithTimestamps): string[] {
  const timestamps: string[] = [update.createdAt, update.statusDate];

  // Add task timestamps
  if (update.tasks) {
    update.tasks.forEach((task) => {
      if (task.completedAt) timestamps.push(task.completedAt);
    });
  }

  // Add approval timestamps
  if (update.approvals) {
    update.approvals.forEach((approval) => {
      timestamps.push(approval.statusDate);
      if (approval.approvedAt) timestamps.push(approval.approvedAt);
      if (approval.rejectedAt) timestamps.push(approval.rejectedAt);
    });
  }

  return timestamps;
}

/**
 * Calculates the time difference between now and a given date/time string
 * and returns a human-readable relative time string using date-fns.
 *
 * @param dateTimeString - ISO 8601 datetime string (e.g., "2023-12-15T10:30:00Z")
 * @returns Human-readable relative time string (e.g., "2 hours ago", "3 days ago")
 */
export function getRelativeTime(dateTimeString: string): string {
  return formatDistanceToNow(new Date(dateTimeString), {
    addSuffix: true,
  });
}

/**
 * Formats a bundle track date (YYYY-MM-DD) as a human-readable label.
 * Parsed as a local date to avoid timezone shifting the calendar day.
 *
 * @param dateString - date in YYYY-MM-DD format (e.g. "2026-06-15")
 * @returns formatted label (e.g. "Jun 15, 2026"), or the original string if invalid
 */
export function formatBundleDate(dateString: string): string {
  const [year, month, day] = dateString.split('-').map(Number);

  if (!year || !month || !day) {
    return dateString;
  }

  return format(new Date(year, month - 1, day), 'MMM d, yyyy');
}

/**
 * Finds the most recent update timestamp from a update object including its tasks and approvals
 *
 * @param update - update object with createdAt, statusDate, tasks, and approvals
 * @returns The most recent timestamp from all available dates
 */
export function getMostRecentUpdateTime(update: UpdateWithTimestamps): string {
  const timestamps = collectTimestamps(update);

  // Find the most recent timestamp
  const mostRecent = timestamps
    .map((ts) => new Date(ts).getTime())
    .reduce((recent, current) => Math.max(recent, current), -Infinity);

  return new Date(mostRecent).toISOString();
}

/**
 * Gets the relative time for the most recent update in a update
 *
 * @param update - update object with createdAt, statusDate, tasks, and approvals
 * @returns Abbreviated relative time string for the most recent update (e.g., "12h ago"), or null if no valid timestamps
 */
export function getLastUpdatedText(
  update: UpdateWithTimestamps,
): string | null {
  const timestamps = collectTimestamps(update);

  if (timestamps.length === 0) {
    return null;
  }

  const mostRecent = timestamps
    .map((ts) => new Date(ts).getTime())
    .reduce((recent, current) => Math.max(recent, current), -Infinity);

  // Handle where all dates are invalid
  if (mostRecent === -Infinity) {
    return null;
  }

  return getRelativeTime(new Date(mostRecent).toISOString());
}
